const DEFAULT_MAX_DIMENSION = 1280
const DEFAULT_TARGET_BYTES = 900000
const DEFAULT_QUALITY_STEPS = [0.92, 0.86, 0.8, 0.74, 0.68, 0.62, 0.56]
const DEFAULT_DIMENSION_STEPS = [1, 0.85, 0.72, 0.6, 0.5]

function toPositiveInt(value, fallback) {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback
}

export function getReceiptImageCompressionSettings() {
  return {
    maxDimension: toPositiveInt(import.meta.env.VITE_RECEIPT_IMAGE_MAX_DIMENSION, DEFAULT_MAX_DIMENSION),
    targetBytes: toPositiveInt(import.meta.env.VITE_RECEIPT_IMAGE_MAX_PAYLOAD_BYTES, DEFAULT_TARGET_BYTES),
    qualitySteps: DEFAULT_QUALITY_STEPS,
    dimensionSteps: DEFAULT_DIMENSION_STEPS,
  }
}

async function fileToDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Failed to read image file'))
    reader.onload = () => resolve(String(reader.result || ''))
    reader.readAsDataURL(file)
  })
}

async function blobToDataUrl(blob) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Failed to encode compressed image'))
    reader.onload = () => resolve(String(reader.result || ''))
    reader.readAsDataURL(blob)
  })
}

async function loadSourceImage(file) {
  if (typeof globalThis.createImageBitmap === 'function') {
    try {
      const bitmap = await globalThis.createImageBitmap(file)
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close?.(),
      }
    } catch {
      // WHY: some browsers cannot decode every mobile image format through createImageBitmap.
    }
  }

  const objectUrl = URL.createObjectURL(file)
  return await new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      resolve({
        source: image,
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
        release: () => URL.revokeObjectURL(objectUrl),
      })
    }
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Failed to decode image file'))
    }
    image.src = objectUrl
  })
}

async function renderToBlob(source, width, height, quality) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) return null

  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, height)
  context.drawImage(source, 0, 0, width, height)

  return await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)
  })
}

function buildDimensionCandidates(width, height, maxDimension, dimensionSteps) {
  const longestSide = Math.max(width, height)
  const baseScale = Math.min(1, maxDimension / longestSide)
  const candidates = dimensionSteps.map((step) => Math.max(0.25, baseScale * step))
  const seen = new Set()
  return candidates
    .map((scale) => ({
      width: Math.max(1, Math.round(width * scale)),
      height: Math.max(1, Math.round(height * scale)),
    }))
    .filter((candidate) => {
      const key = `${candidate.width}x${candidate.height}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

export async function prepareReceiptImageUpload(file, settings = {}, deps = {}) {
  const resolvedSettings = {
    ...getReceiptImageCompressionSettings(),
    ...settings,
  }
  const fileToDataUrlImpl = deps.fileToDataUrl ?? fileToDataUrl
  const loadSourceImageImpl = deps.loadSourceImage ?? loadSourceImage
  const renderToBlobImpl = deps.renderToBlob ?? renderToBlob
  const blobToDataUrlImpl = deps.blobToDataUrl ?? blobToDataUrl

  let rawDataUrl = null
  const getRawDataUrl = async () => {
    if (!rawDataUrl) {
      rawDataUrl = await fileToDataUrlImpl(file)
    }
    return rawDataUrl
  }

  let source = null
  try {
    source = await loadSourceImageImpl(file)
  } catch {
    return {
      dataUrl: await getRawDataUrl(),
      mimeType: file.type || 'image/jpeg',
      originalBytes: file.size,
      outputBytes: file.size,
      wasCompressed: false,
    }
  }

  try {
    const needsCompression =
      file.size > resolvedSettings.targetBytes ||
      source.width > resolvedSettings.maxDimension ||
      source.height > resolvedSettings.maxDimension

    if (!needsCompression) {
      return {
        dataUrl: await getRawDataUrl(),
        mimeType: file.type || 'image/jpeg',
        originalBytes: file.size,
        outputBytes: file.size,
        wasCompressed: false,
      }
    }

    const dimensionCandidates = buildDimensionCandidates(
      source.width,
      source.height,
      resolvedSettings.maxDimension,
      resolvedSettings.dimensionSteps,
    )

    for (const candidate of dimensionCandidates) {
      for (const quality of resolvedSettings.qualitySteps) {
        let blob = null
        try {
          blob = await renderToBlobImpl(source.source, candidate.width, candidate.height, quality)
        } catch {
          continue
        }
        if (!blob || blob.size > resolvedSettings.targetBytes) {
          continue
        }
        try {
          return {
            dataUrl: await blobToDataUrlImpl(blob),
            mimeType: 'image/jpeg',
            originalBytes: file.size,
            outputBytes: blob.size,
            wasCompressed: true,
          }
        } catch {
          continue
        }
      }
    }
  } finally {
    source?.release()
  }

  return {
    dataUrl: await getRawDataUrl(),
    mimeType: file.type || 'image/jpeg',
    originalBytes: file.size,
    outputBytes: file.size,
    wasCompressed: false,
  }
}
